/**
 * Firebase Analytics lifecycle (consent-gated on web).
 *
 * Flow:
 *   Usercentrics firebase DPS (kjk3gPD2cxt_Ea) → TrackingInitializer → init / teardown here
 *   firebaseLogs → getFirebaseAnalytics() + isFirebaseAnalyticsCollectionEnabled() guard
 *
 * Consent for this DPS drives all Firebase / GA4 collection and the Google Consent Mode
 * signals the Firebase SDK uses (analytics + ads).
 */
import { USERCENTRICS_ENABLE } from 'config/env';
import { initializeApp } from 'firebase/app';
import {
  Analytics,
  getAnalytics,
  isSupported,
  setAnalyticsCollectionEnabled,
} from 'firebase/analytics';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FB_API_KEY,
  authDomain: process.env.REACT_APP_FB_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FB_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FB_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FB_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FB_APP_ID,
  measurementId: process.env.REACT_APP_FB_MEASUREMENT_ID,
};

type GtagWindow = Window & {
  gtag?: (...args: unknown[]) => void;
  dataLayer?: unknown[];
};

/**
 * Sync Google Consent Mode v2 with the Firebase Analytics DPS grant/revoke.
 * All signals are toggled together — one Usercentrics service controls Firebase collection.
 */
export const updateGoogleConsentForFirebase = (granted: boolean): void => {
  if (typeof window === 'undefined') return;

  const w = window as GtagWindow;
  w.dataLayer = w.dataLayer || [];

  const state = granted ? 'granted' : 'denied';
  const consentUpdate = {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  };

  if (w.gtag) {
    w.gtag('consent', 'update', consentUpdate);
    return;
  }

  w.dataLayer.push(['consent', 'update', consentUpdate]);
};

const app = initializeApp(firebaseConfig);

// Singleton Analytics instance; collection on/off is controlled separately below.
let analyticsInstance: Analytics | null = null;
// Dedupes concurrent initFirebaseAnalytics() calls (e.g. rapid consent updates).
let analyticsInitPromise: Promise<Analytics | null> | null = null;
// Sync mirror for firebaseLogs — flipped before async SDK calls on revoke.
let isCollectionEnabled = !USERCENTRICS_ENABLE;

/** Sync guard for firebaseLogs. False until consent grant when CMP is enabled. */
export const isFirebaseAnalyticsCollectionEnabled = (): boolean => {
  if (!USERCENTRICS_ENABLE) return true;
  return isCollectionEnabled;
};

const enableAnalyticsCollection = async (analytics: Analytics): Promise<void> => {
  updateGoogleConsentForFirebase(true);
  await setAnalyticsCollectionEnabled(analytics, true);
  isCollectionEnabled = true;
  updateGoogleConsentForFirebase(true);
};

/** Consent grant: create Analytics once, then enable collection. Safe to call on re-grant after revoke. */
export const initFirebaseAnalytics = async (): Promise<Analytics | null> => {
  if (analyticsInstance) {
    await enableAnalyticsCollection(analyticsInstance);
    return analyticsInstance;
  }
  if (analyticsInitPromise) return analyticsInitPromise;

  analyticsInitPromise = (async () => {
    const supported = await isSupported();
    if (!supported) return null;

    analyticsInstance = getAnalytics(app);
    await enableAnalyticsCollection(analyticsInstance);
    return analyticsInstance;
  })();

  return analyticsInitPromise;
};

/**
 * Consent revoke: stop collection. Does not destroy the instance — re-grant calls init again
 * and flips collection back on.
 */
export const teardownFirebaseAnalytics = async (): Promise<void> => {
  isCollectionEnabled = false;
  updateGoogleConsentForFirebase(false);

  const analytics = analyticsInstance ?? (analyticsInitPromise ? await analyticsInitPromise : null);
  if (!analytics) return;

  await setAnalyticsCollectionEnabled(analytics, false);
};

/** Used by firebaseLogs; returns null until initFirebaseAnalytics() has completed. */
export const getFirebaseAnalytics = (): Analytics | null => analyticsInstance;
